import { effectiveBeginningScenarioText, parseBeginningScenario } from "./careerScenario";
import { DEFAULT_CAREER, ensureCareerSeedAirports, inboxFromCareerSeed, openingAccount } from "./career";
import { type CareerSavePayload } from "./careerSave";
import { DEFAULT_COMPANY } from "./company";
import { defaultAccount, emptyAccount, openingCapitalFromAccount } from "./ledger";
import { refreshFleetStatus } from "./maintenance";
import type {
  AircraftSpec,
  CareerState,
  CharterRequest,
  CompanyAccount,
  CompanyProfile,
  DutyStandbyState,
  PilotProfile,
  SceneryAirport,
  ViewId,
} from "./types";
import type { DutyRoster } from "./duty";

/** Type-to-confirm token for Settings → Developer → Reset progress. */
export const RESET_PROGRESS_PHRASE = "RESET";

/** Type-to-confirm token for Settings → Developer → Start from scratch. */
export const START_FROM_SCRATCH_PHRASE = "WIPE";

export type ProgressResetSource = Pick<
  CareerSavePayload,
  | "company"
  | "profile"
  | "fleet"
  | "account"
  | "career"
  | "duty"
  | "dutyStandby"
  | "scenery"
  | "crew"
  | "rates"
  | "catalogs"
  | "catalogStubs"
  | "jobMode"
  | "sopOverrides"
>;

export type ProgressResetResult = CareerSavePayload & {
  view: ViewId;
  notice: string;
};

export function resetProgressPhraseOk(typed: string): boolean {
  return typed.trim().toUpperCase() === RESET_PROGRESS_PHRASE;
}

export function startFromScratchPhraseOk(typed: string): boolean {
  return typed.trim().toUpperCase() === START_FROM_SCRATCH_PHRASE;
}

export { openingCapitalFromAccount } from "./ledger";

function fleetRowsFromSpecs(fleet: AircraftSpec[]) {
  return fleet.map((spec) => ({
    icaoType: spec.icaoType,
    registration: spec.registration,
    name: spec.name,
  }));
}

function parkFleet(fleet: AircraftSpec[], company: CompanyProfile, primary?: { registration: string; id: string; startIcao: string }): AircraftSpec[] {
  const home = company.homeIcao;
  return fleet.map((spec) => {
    const isPrimary =
      primary &&
      (spec.registration.toUpperCase() === primary.registration.toUpperCase() || spec.id === primary.id);
    return {
      ...spec,
      status: "available" as const,
      available: true,
      lastAirportIcao: isPrimary ? primary.startIcao : home,
      fuelOnBoardGal: undefined,
    };
  });
}

function coldStandby(current: DutyStandbyState | undefined): DutyStandbyState {
  return {
    armed: false,
    armedAtUtc: undefined,
    windows: current?.windows ?? [],
    seenInboxIds: [],
  };
}

function keepDutySettings(duty: DutyRoster): DutyRoster {
  return { settings: duty.settings, sessions: [] };
}

function careerAfterReset(career: CareerState, playMode: CareerState["playMode"], beginningNotes: string): CareerState {
  return {
    ...career,
    playMode,
    scenarioConfirmed: playMode === "career" || playMode === "casual" ? true : career.scenarioConfirmed,
    beginningNotes: playMode === "career" ? beginningNotes : career.beginningNotes,
  };
}

function wizardFromDesk(company: CompanyProfile, profile: PilotProfile, fleet: AircraftSpec[], capital: number) {
  return {
    operatorName: company.operatorName,
    homeIcao: company.homeIcao,
    startingCapitalCad: capital,
    fleetRows: fleetRowsFromSpecs(fleet),
    adminName: profile.name,
  };
}

function careerInbox(
  company: CompanyProfile,
  profile: PilotProfile,
  fleet: AircraftSpec[],
  career: CareerState,
  capital: number,
  scenery: SceneryAirport[],
  now: Date,
): {
  inbox: CharterRequest[];
  fleet: AircraftSpec[];
  account: CompanyAccount;
  notes: string;
  scenery: SceneryAirport[];
} {
  const notes = effectiveBeginningScenarioText(career.beginningNotes);
  const seed = parseBeginningScenario(notes, wizardFromDesk(company, profile, fleet, capital));
  const parked = parkFleet(fleet, company, {
    registration: seed.primaryRegistration,
    id: seed.primaryAircraftId,
    startIcao: seed.startIcao,
  });
  const amount = capital > 0 ? capital : seed.startingCapitalCad;
  const pair = {
    origin: seed.firstJob.originIcao.trim(),
    dest: seed.firstJob.destIcao.trim(),
  };
  const inbox =
    pair.origin && pair.dest && seed.primaryRegistration
      ? [inboxFromCareerSeed({ ...seed, operatorName: company.operatorName }, parked, now)]
      : [];
  return {
    inbox,
    fleet: parked,
    account: openingAccount(amount, now),
    notes,
    scenery: ensureCareerSeedAirports(scenery, seed),
  };
}

/**
 * Reset in-app career / ops progress. Keeps company, crew, destinations,
 * rate tables, catalogs, SOP overrides, job mode, and duty / standby settings.
 * Does not touch Settings secrets, phone, or Nanode account records — those
 * live outside this payload.
 */
export function resetCareerProgress(source: ProgressResetSource, now = new Date()): ProgressResetResult {
  const playMode = source.career.playMode;
  const capital = openingCapitalFromAccount(source.account, 0);
  const duty = keepDutySettings(source.duty);
  const dutyStandby = coldStandby(source.dutyStandby);

  if (playMode === "career") {
    const next = careerInbox(
      source.company,
      source.profile,
      source.fleet,
      source.career,
      capital,
      source.scenery,
      now,
    );
    return {
      company: source.company,
      crew: source.crew,
      profile: source.profile,
      scenery: next.scenery,
      rates: source.rates,
      catalogs: source.catalogs,
      catalogStubs: source.catalogStubs,
      jobMode: source.jobMode,
      sopOverrides: source.sopOverrides,
      account: next.account,
      fleet: refreshFleetStatus(next.fleet, [], []),
      missions: [],
      inbox: next.inbox,
      flightLogs: [],
      squawks: [],
      melItems: [],
      logbook: [],
      duty,
      dutyStandby,
      lastKnownPosition: null,
      flightTracks: [],
      assignments: [],
      assignmentAudit: [],
      declineCooldowns: [],
      career: careerAfterReset(source.career, "career", next.notes),
      view: "inbox",
      notice: "RESET  career progress",
    };
  }

  return {
    company: source.company,
    crew: source.crew,
    profile: source.profile,
    scenery: source.scenery,
    rates: source.rates,
    catalogs: source.catalogs,
    catalogStubs: source.catalogStubs,
    jobMode: source.jobMode,
    sopOverrides: source.sopOverrides,
    account: capital > 0 ? defaultAccount(now, capital) : emptyAccount(now, source.account?.homeCurrency),
    fleet: refreshFleetStatus(parkFleet(source.fleet, source.company), [], []),
    missions: [],
    inbox: [],
    flightLogs: [],
    squawks: [],
    melItems: [],
    logbook: [],
    duty,
    dutyStandby,
    lastKnownPosition: null,
    flightTracks: [],
    assignments: [],
    assignmentAudit: [],
    declineCooldowns: [],
    career: careerAfterReset(source.career, playMode, source.career.beginningNotes),
    view: "dashboard",
    notice: playMode === "casual" ? "RESET  casual ops progress" : "RESET  ops progress",
  };
}

function emptyOpsBoard(
  source: ProgressResetSource,
  extras: Pick<ProgressResetResult, "company" | "crew" | "career" | "account" | "fleet" | "notice">,
): ProgressResetResult {
  return {
    company: extras.company,
    crew: extras.crew,
    profile: source.profile,
    scenery: source.scenery,
    rates: source.rates,
    catalogs: source.catalogs,
    catalogStubs: source.catalogStubs,
    jobMode: source.jobMode,
    sopOverrides: source.sopOverrides,
    account: extras.account,
    fleet: extras.fleet,
    missions: [],
    inbox: [],
    flightLogs: [],
    squawks: [],
    melItems: [],
    logbook: [],
    duty: keepDutySettings(source.duty),
    dutyStandby: coldStandby(source.dutyStandby),
    lastKnownPosition: null,
    flightTracks: [],
    assignments: [],
    assignmentAudit: [],
    declineCooldowns: [],
    career: extras.career,
    view: "dashboard",
    notice: extras.notice,
  };
}

/**
 * Empty Inbox / board / logs for a Nanode Push. Company shell, crew, and
 * play-mode stay so other desks keep their configured company — not a first-run
 * snapshot. Does not touch Settings secrets (they are not on this payload).
 */
export function emptyCompanyBoardForNanode(source: ProgressResetSource, now = new Date()): ProgressResetResult {
  const playMode = source.career.playMode;
  const capital = openingCapitalFromAccount(source.account);
  const parked = parkFleet(source.fleet, source.company);
  return emptyOpsBoard(source, {
    company: source.company,
    crew: source.crew,
    career: {
      ...source.career,
      beginningNotes: "",
      scenarioConfirmed: playMode === "career" || playMode === "casual",
    },
    account: defaultAccount(now, capital),
    fleet: refreshFleetStatus(parked, [], []),
    notice: "WIPE  empty company board for Nanode",
  });
}

/**
 * Start from scratch on this PC. Clears the first-run gates
 * (`company.setupComplete` and `career.playMode`) so the existing opening
 * prompt — start a company vs casual vs Join — appears again. Empties Inbox /
 * board / logs / crew logins. Unlike Reset progress, this does not keep the
 * company shell or reseed the Career opening job.
 *
 * Settings secrets (xAI, SimBrief, Twilio / phone, sync URL + token) live
 * outside this payload and must stay on the desk.
 */
export function wipeDeskOpsFromScratch(source: ProgressResetSource, now = new Date()): ProgressResetResult {
  return emptyOpsBoard(source, {
    company: DEFAULT_COMPANY,
    crew: [],
    career: DEFAULT_CAREER,
    account: emptyAccount(now),
    fleet: [],
    notice: "WIPE  start from scratch — first-run",
  });
}

export function resetCareerSaveProgress(payload: CareerSavePayload, now = new Date()): CareerSavePayload {
  const next = resetCareerProgress(payload, now);
  return {
    company: next.company,
    crew: next.crew,
    profile: next.profile,
    account: next.account,
    fleet: next.fleet,
    scenery: next.scenery,
    missions: next.missions,
    inbox: next.inbox,
    flightLogs: next.flightLogs,
    rates: next.rates,
    squawks: next.squawks,
    melItems: next.melItems,
    logbook: next.logbook,
    duty: next.duty,
    career: next.career,
    catalogStubs: next.catalogStubs,
    catalogs: next.catalogs,
    dutyStandby: next.dutyStandby,
    jobMode: next.jobMode,
    lastKnownPosition: next.lastKnownPosition,
    flightTracks: next.flightTracks,
    sopOverrides: next.sopOverrides,
    assignments: next.assignments ?? [],
    assignmentAudit: next.assignmentAudit ?? [],
    declineCooldowns: next.declineCooldowns ?? [],
  };
}
